import { DocumentModel, DocumentBlock, DocumentSection, CURRENT_SCHEMA_VERSION } from './document.types';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedDocument?: DocumentModel;
}

/**
 * Validates a document AST against required invariants and sanitizes damaged properties safely
 */
export function validateAndSanitizeDocument(doc: any): ValidationResult {
  const errors: string[] = [];

  if (!doc || typeof doc !== 'object') {
    return { isValid: false, errors: ['Document must be a valid non-null object.'] };
  }

  if (typeof doc.schemaVersion !== 'number') {
    errors.push('Missing schemaVersion.');
  }

  if (!doc.metadata || typeof doc.metadata !== 'object') {
    errors.push('Missing document metadata.');
  }

  if (!doc.pageSettings || typeof doc.pageSettings !== 'object') {
    errors.push('Missing page settings.');
  }

  if (!Array.isArray(doc.sections)) {
    errors.push('Sections must be an array.');
  }

  if (errors.length > 0) {
    return { isValid: false, errors };
  }

  // Sanitize sections and blocks
  const sanitizedSections: DocumentSection[] = (doc.sections as any[]).map((sec, secIdx) => {
    const validBlocks: DocumentBlock[] = Array.isArray(sec.blocks)
      ? sec.blocks.map((blk: any, blkIdx: number): DocumentBlock => {
          const blockId = typeof blk.id === 'string' && blk.id ? blk.id : `blk-sanitized-${secIdx}-${blkIdx}-${Date.now()}`;
          const createdAt = typeof blk.createdAt === 'string' ? blk.createdAt : new Date().toISOString();

          switch (blk.type) {
            case 'text':
              return {
                id: blockId,
                type: 'text',
                createdAt,
                data: {
                  text: typeof blk.data?.text === 'string' ? blk.data.text : '',
                  formatting: blk.data?.formatting || {},
                },
              };
            case 'formula':
              return {
                id: blockId,
                type: 'formula',
                createdAt,
                data: {
                  latex: typeof blk.data?.latex === 'string' ? blk.data.latex : 'x = 0',
                  displayMode: typeof blk.data?.displayMode === 'boolean' ? blk.data.displayMode : true,
                  caption: blk.data?.caption,
                  equationNumber: blk.data?.equationNumber,
                },
              };
            case 'table':
              return {
                id: blockId,
                type: 'table',
                createdAt,
                data: {
                  rows: typeof blk.data?.rows === 'number' ? blk.data.rows : 2,
                  cols: typeof blk.data?.cols === 'number' ? blk.data.cols : 2,
                  hasHeaderRow: !!blk.data?.hasHeaderRow,
                  hasHeaderCol: !!blk.data?.hasHeaderCol,
                  cells: Array.isArray(blk.data?.cells) ? blk.data.cells : [[{ id: 'c1', content: '' }]],
                  caption: blk.data?.caption,
                  borderStyle: blk.data?.borderStyle || 'academic',
                },
              };
            case 'image':
              return {
                id: blockId,
                type: 'image',
                createdAt,
                data: {
                  assetId: typeof blk.data?.assetId === 'string' ? blk.data.assetId : '',
                  previewDataUrl: blk.data?.previewDataUrl,
                  caption: blk.data?.caption,
                  altText: blk.data?.altText,
                  widthPercent: typeof blk.data?.widthPercent === 'number' ? blk.data.widthPercent : 80,
                  alignment: blk.data?.alignment || 'center',
                },
              };
            case 'page_break':
              return {
                id: blockId,
                type: 'page_break',
                createdAt,
                data: {
                  forced: true,
                },
              };
            default:
              // Fallback for unknown block types
              return {
                id: blockId,
                type: 'text',
                createdAt,
                data: {
                  text: `[Unsupported block type: ${blk.type}]`,
                },
              };
          }
        })
      : [];

    return {
      id: typeof sec.id === 'string' && sec.id ? sec.id : `sec-sanitized-${secIdx}-${Date.now()}`,
      title: typeof sec.title === 'string' ? sec.title : `Section ${secIdx + 1}`,
      type: sec.type || 'custom',
      enabled: typeof sec.enabled === 'boolean' ? sec.enabled : true,
      collapsed: !!sec.collapsed,
      settings: sec.settings || {},
      blocks: validBlocks,
    };
  });

  const sanitizedDocument: DocumentModel = {
    schemaVersion: doc.schemaVersion || CURRENT_SCHEMA_VERSION,
    metadata: {
      id: doc.metadata.id || `doc-${Date.now()}`,
      title: doc.metadata.title || 'Untitled Document',
      subtitle: doc.metadata.subtitle,
      documentType: doc.metadata.documentType || 'lab_report',
      courseCode: doc.metadata.courseCode,
      courseTitle: doc.metadata.courseTitle,
      author: doc.metadata.author || { studentName: 'Student' },
      instructor: doc.metadata.instructor || {},
      institution: doc.metadata.institution || { name: 'Institution', shortName: 'INST' },
      createdAt: doc.metadata.createdAt || new Date().toISOString(),
      modifiedAt: doc.metadata.modifiedAt || new Date().toISOString(),
      submissionDate: doc.metadata.submissionDate,
      version: doc.metadata.version || 1,
    },
    pageSettings: doc.pageSettings,
    sections: sanitizedSections,
  };

  return { isValid: true, errors: [], sanitizedDocument };
}
